// Maps a docs-center group key to its TanStack Router route templates. The
// `to` prop must be a literal route id, so per-group templates are enumerated
// here and shared by every group-aware docs component (sidebar, breadcrumb,
// home cards). Keep these in sync with the route files and with the
// `DOC_GROUPS` config in vite-plugins/docs-index.ts.

export const groupIndexRoute = {
  documentation: '/docs',
  developers: '/developers',
  'about-legal': '/about-legal',
} as const;

export const groupSectionRoute = {
  documentation: '/docs/$section',
  developers: '/developers/$section',
  'about-legal': '/about-legal/$section',
} as const;

export const groupPageRoute = {
  documentation: '/docs/$section/$slug',
  developers: '/developers/$section/$slug',
  'about-legal': '/about-legal/$section/$slug',
} as const;

export type DocGroupKey = keyof typeof groupIndexRoute;

// The newest developer API version. The developers landing and sidebar show this
// version's API sections; older versions stay reachable at their own URLs. Bump
// this (and add the matching `developers.v2.*` route files + content) for v2.
export const LATEST_DEVELOPER_VERSION = 'v1';

// Versioned developer API routes live under a static `/developers/v1` segment
// (one entry per version). Unversioned developer sections (e.g. Overview) keep
// using the group* maps above. Params for section/page links are always
// { section, slug } — the version is baked into the literal route id.
const developerVersionRoutes = {
  v1: {
    index: '/developers/v1',
    section: '/developers/v1/$section',
    page: '/developers/v1/$section/$slug',
  },
} as const;

type DeveloperVersion = keyof typeof developerVersionRoutes;

function isDeveloperVersion(version?: string): version is DeveloperVersion {
  return version != null && version in developerVersionRoutes;
}

/** Route template for a doc page link, accounting for versioned dev sections. */
export function docPageRoute(group: DocGroupKey, version?: string) {
  if (group === 'developers' && isDeveloperVersion(version)) {
    return developerVersionRoutes[version].page;
  }
  return groupPageRoute[group];
}

/** Route template for a doc section landing link, accounting for versions. */
export function docSectionRoute(group: DocGroupKey, version?: string) {
  if (group === 'developers' && isDeveloperVersion(version)) {
    return developerVersionRoutes[version].section;
  }
  return groupSectionRoute[group];
}

/** Route for a version landing (e.g. /developers/v1); group index otherwise. */
export function docVersionIndexRoute(group: DocGroupKey, version?: string) {
  if (group === 'developers' && isDeveloperVersion(version)) {
    return developerVersionRoutes[version].index;
  }
  return groupIndexRoute[group];
}

/** Narrow an arbitrary group string to a known key, defaulting to documentation. */
export function asGroupKey(group: string): DocGroupKey {
  return group in groupIndexRoute ? (group as DocGroupKey) : 'documentation';
}

/**
 * Groups whose sidebar sections all start expanded on navigation, rather than
 * opening just one. Off by default; About & Legal opts in because its sections
 * are short and readers tend to scan across all of them at once.
 */
const EXPAND_ALL_SECTIONS_GROUPS: ReadonlySet<DocGroupKey> = new Set(['about-legal']);

/** Whether a group shows every sidebar section expanded by default. */
export function groupExpandsAllSections(group: DocGroupKey): boolean {
  return EXPAND_ALL_SECTIONS_GROUPS.has(group);
}

/** Human label for a docs-center group; evaluated at call time for i18n. */
export function getGroupLabel(group: DocGroupKey): string {
  switch (group) {
    case 'developers':
      return 'Developers';
    case 'about-legal':
      return 'About & Legal';
    case 'documentation':
    default:
      return 'Documentation';
  }
}

/**
 * The developer API version segment in a path, e.g. 'v1' for
 * `/developers/v1/...`, or undefined for unversioned developer paths.
 */
export function developerVersionFromPath(pathname: string): string | undefined {
  const parts = pathname.split('/').filter(Boolean); // [developers, seg, …]
  const seg = parts[1];
  return seg && /^v\d+$/.test(seg) ? seg : undefined;
}

/** Which group a path belongs to, or null when outside the docs center. */
export function activeGroupFromPath(pathname: string): DocGroupKey | null {
  if (pathname === '/developers' || pathname.startsWith('/developers/'))
    return 'developers';
  if (pathname === '/about-legal' || pathname.startsWith('/about-legal/'))
    return 'about-legal';
  if (pathname === '/docs' || pathname.startsWith('/docs/')) return 'documentation';
  return null;
}
